import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ReactComponent as TabletLogo } from "../../assets/images/logo/img-logo-white_no_text.svg";
import { ReactComponent as DesktopLogo } from "../../assets/images/logo/img-logo-white_straight.svg";
import context from "../../pages/useContext/MenuContext";

import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { getMenuIcon } from "../../router/menuIconMapper";
import { getMenuRouter } from "../../router/menuRouterMapper";
import { SystemLoading } from "../../store/loadingStore";
import { MessageContext } from "../../store/messageStore";
import { AlertService, AlertType } from "../../utility/service/alert-service";
import { updateUserLoginRecord } from "../../utility/service/api-service";
import { revocationToken } from "../../utility/service/dgr-service";
import { getfuncListAndLoginInfo } from "../../utility/service/personal-service";
import { checkDpSuccess, handleSessionClear, toggleClassName, writeToken } from "../../utility/service/tool-service";
import { AcctContext } from "../../utility/useReduce/userNameReduce";
import SideBarMenuSkeleton from "./SideBarMenuSkeleton";
import { UserAvatarButton } from "./UserAvatarButton";

export default function SideBar({ meunFunctionId }) {
  const [, loading] = useContext(SystemLoading);
  const [, dispatch] = useContext(MessageContext); //訊息視窗

  const [menuList, setMenuList] = useState([]);
  const [managerFunctions, setManagerFunctions] = useState([]);
  const { currentFunctionId, setCurrentFunctionId } = useContext(context);
  const navigate = useNavigate();
  const [currentParentFunctionId, setCurrentParentFunctionId] = useState("");
  // 不顯示在Menu上的functionId
  const excludeList = useMemo(() => ["F001", "F002", "F199"], []);
  const { t } = useTranslation();
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [userName, setUserName] = useState("");
  const [state, dispatchUser] = useContext(AcctContext); //訊息視窗

  useEffect(() => {
    // const _userName = JSON.parse(sessionStorage?.getItem('decode_token'))?.['name']
    // if (_userName) setUserName(_userName);
    setUserName(state?.userName);
  }, [state]);

  useEffect(() => {
    if (state.reflashFlag) {
      axios_getfuncListAndLoginInfo();
      dispatchUser({ type: "REFLASH_FLAG", payload: { reflashFlag: !state.reflashFlag } });
    }
  }, [state.reflashFlag]);

  // 過濾掉excludeList中的functionId
  const filterFunctions = useCallback((functions, excludeList) => {
    return functions?.filter((func) => {
      if (excludeList?.includes(func.functionId)) {
        return false;
      }

      if (func?.subFunctions?.length > 0) {
        func.subFunctions = filterFunctions(func.subFunctions, excludeList);
      }

      return true;
    });
  }, []);

  const axios_getfuncListAndLoginInfo = () => {
    setSidebarLoading(true);
    getfuncListAndLoginInfo()
      .then((res) => {
        if (checkDpSuccess(res)) {
          let data = res?.data?.RespBody;
          const functions = res.data.RespBody.functions;
          const managerFunctions = res?.data?.RespBody?.managerFunctions || [];
          setMenuList(functions);
          setManagerFunctions(managerFunctions);

          // 建立 editableFlags 和 tabPermissions 對應表（遞迴處理 subFunctions）
          const editableFlags = {};
          const tabPermissions = {};
          const processFunctions = (funcList) => {
            funcList.forEach((func) => {
              editableFlags[func.functionId] = func.editableFlag || "N";
              tabPermissions[func.functionId] = func.tabPermissions || null;
              if (func.subFunctions && func.subFunctions.length > 0) {
                processFunctions(func.subFunctions);
              }
            });
          };
          processFunctions(functions);
          // 也處理 managerFunctions 以便將 B 系列功能的 editableFlag 和 tabPermissions 納入全域狀態
          if (managerFunctions && managerFunctions.length > 0) {
            processFunctions(managerFunctions);
          }

          writeToken("companyType", data.companyType);
          writeToken("companySeq", data.companySeq);
          writeToken("userName", data.userName);
          writeToken("acct", data?.acct);
          dispatchUser({ type: "CHANGE_NAME", payload: data.userName });
          dispatchUser({ type: "COMPANY_TYPE", payload: data.companyType });
          dispatchUser({
            type: "IS_ADMIN_INFO",
            payload: { isAdmin: data.isAdmin, effectiveType: data.effectiveType, companySeq: data.companySeq },
          });
          dispatchUser({ type: "COM_CONTRACT_DATA", payload: { companiesContractData: data.companiesContractData } });
          dispatchUser({ type: "DONT_SHOW_AGAIN", payload: { dontShowAgain: data.dontShowAgain } });
          dispatchUser({ type: "APPROVE_FLAG", payload: { approveFlag: data.approveFlag === "Y" } });
          // 儲存功能清單、可編輯狀態和頁籤權限
          dispatchUser({ type: "SET_FUNCTIONS", payload: { functions, editableFlags, tabPermissions } });
          // 儲存用戶角色信息
          if (data.userRoles) {
            dispatchUser({ type: "SET_USER_ROLES", payload: { userRoles: data.userRoles } });
          }

          if (data?.changePcodeMsg !== null && data?.changePcodeMsg !== undefined) {
            AlertService.ask({
              title: t("changePwd"),
              text: data?.changePcodeMsg,
              type: AlertType.info,
              showCancelButton: true,
              cancelButtonText: t("button.cancel"),
              confirmButtonText: t("button.confirm"),
              preConfirm: () => {
                navigate("/management/changePcode");
                dispatchUser({ type: "CURRENT_FUNCID", payload: "F10202" });
              },
            });
          }
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally((fin) => {
        setSidebarLoading(false);
      });
  };

  useEffect(() => {
    axios_getfuncListAndLoginInfo();
  }, []);

  /**
   * 取得當前路由MenuItem最父層的functionId
   */
  const findCurrentParentFunctionId = useCallback((menuList, currentFunctionId) => {
    return (
      menuList.find((item) => {
        if (item.functionId === currentFunctionId) {
          return true;
        } else {
          if (item?.subFunctions?.length > 0) {
            return findCurrentParentFunctionId(item.subFunctions, currentFunctionId);
          }
        }
        return false;
      })?.functionId || ""
    );
  }, []);

  /**
   * @description 找尋帶有特定className的父層元素
   * @param ele 作為基準的指定元素
   * @param className 預期找到的父層所包含的className
   * @returns
   */
  const findParentEleByClassName = (ele, className) => {
    if (ele && ele.classList.contains(className)) {
      return ele;
    } else {
      return ele ? findParentEleByClassName(ele.parentElement, className) : null;
    }
  };

  /**
   * 移除所有active class
   */
  const removeAllActiveClass = useCallback((className = "a.menu-link.active") => {
    const menuBgList = document.querySelectorAll(className);
    menuBgList.forEach((ele) => {
      ele.classList.remove("active");
    });
  }, []);

  const handleMenuClick = (event, level = 1, currentFunId = "") => {
    const ele = event.target;
    const parentEle = findParentEleByClassName(ele, "menu-link");
    if (parentEle) {
      removeAllActiveClass();
      toggleClassName(parentEle, "active");
    }
  };

  const handleLogout = async () => {
    //註銷token access_token refresh_token
    await updateUserLoginRecord({ eventType: "LOGOUT" });
    await revocationToken("access_token")
      .then((res) => {
        if (res?.status === 200) {
          dispatchUser({ type: "CLEAR_INFO" });
          return revocationToken("refresh_token");
        }
      })
      .then((res) => {
        if (res?.status === 200) {
          handleSessionClear(); //不要將loadTheme刪除 系統設定的主題色
          navigate("/login");
        }
      });
  };
  return (
    <>
      <div className="layout-sidebar">
        <div className="sidebar-desktop-header hidden lg:flex justify-content-center align-items-center">
          <DesktopLogo className="img-desktop-logo" />
          <TabletLogo className="img-tablet-logo" />
        </div>
        <div className="sidebar-mobile-header lg:hidden block justify-content-center align-items-center">
          <div className="mobile-header-content">
            <UserAvatarButton type="sideBar" />
          </div>
        </div>
        <div className="sidebar-body">
          <ul className="sidebar-menu">
            {/* fix: loading 時候請開啟這邊 */}
            {sidebarLoading === true && <SideBarMenuSkeleton />}

            {menuList?.map((item, index) => {
              const Icon = getMenuIcon(item.functionId);
              return item?.type === "expandable" ? (
                <li
                  key={item?.functionId}
                  className="menu-item"
                  onClick={(event) => {
                    handleMenuClick(event, 2);
                    setCurrentParentFunctionId(item?.functionId);
                    dispatchUser({ type: "CURRENT_PARENT_FUNCID", payload: item?.functionId });
                  }}
                >
                  <a className={`menu-link ${state.parentFuncId === item?.functionId ? "active" : ""}`}>
                    <Icon className="menu-icon" />
                    {/* <img className='menu-icon' src={getMenuIcon(item.functionId)} alt='左側選單圖示' /> */}
                    <span>{item?.name}</span>
                  </a>
                  <div className="menu-second">
                    {item?.type === "expandable" && (
                      <>
                        {item?.subFunctions?.map((subItem) => {
                          return (
                            <Link
                              onClick={(event) => {
                                handleMenuClick(event, 2, subItem?.functionId);
                                setCurrentFunctionId(subItem?.functionId);
                                dispatchUser({ type: "CURRENT_FUNCID", payload: subItem?.functionId });
                              }}
                              key={subItem?.functionId}
                              className={`menu-link ${state.funcId === subItem?.functionId ? "active" : ""}`}
                              to={getMenuRouter(subItem?.functionId)}
                            >
                              <span>{subItem.name}</span>
                            </Link>
                          );
                        })}
                      </>
                    )}
                  </div>
                </li>
              ) : (
                <li
                  key={item.functionId}
                  className="menu-item"
                  onClick={(event) => {
                    handleMenuClick(event, 1, item?.functionId);
                    setCurrentFunctionId(item?.functionId);
                    dispatchUser({ type: "CURRENT_FUNCID", payload: item?.functionId });
                  }}
                >
                  <Link
                    key={item?.functionId}
                    className={`menu-link ${state.funcId === item?.functionId ? "active" : ""}`}
                    to={getMenuRouter(item?.functionId)}
                  >
                    <Icon className="menu-icon" />
                    {/* <img className='menu-icon' src={getMenuIcon(item?.functionId)} alt='左側選單圖示' /> */}
                    <span>{item?.name}</span>
                  </Link>
                </li>
              );
            })}
            {managerFunctions.length > 0 && (
              <li>
                {" "}
                <hr />{" "}
              </li>
            )}
            {managerFunctions?.map((item, index) => {
              const Icon = getMenuIcon(item.functionId);
              return item?.type === "expandable" ? (
                <li
                  key={item?.functionId}
                  className="menu-item"
                  onClick={(event) => {
                    handleMenuClick(event, 2);
                    setCurrentParentFunctionId(item?.functionId);
                    dispatchUser({ type: "CURRENT_PARENT_FUNCID", payload: item?.functionId });
                  }}
                >
                  <a className={`menu-link ${state.parentFuncId === item?.functionId ? "active" : ""}`}>
                    <Icon className="menu-icon" />
                    {/* <img className='menu-icon' src={getMenuIcon(item.functionId)} alt='左側選單圖示' /> */}
                    <span>{item?.name}</span>
                  </a>
                  <div className="menu-second">
                    {item?.type === "expandable" && (
                      <>
                        {item?.subFunctions?.map((subItem) => {
                          return (
                            <Link
                              onClick={(event) => {
                                handleMenuClick(event, 2, subItem?.functionId);
                                setCurrentFunctionId(subItem?.functionId);
                                dispatchUser({ type: "CURRENT_FUNCID", payload: subItem?.functionId });
                              }}
                              key={subItem?.functionId}
                              className={`menu-link ${state.funcId === subItem?.functionId ? "active" : ""}`}
                              to={getMenuRouter(subItem?.functionId)}
                            >
                              <span>{subItem.name}</span>
                            </Link>
                          );
                        })}
                      </>
                    )}
                  </div>
                </li>
              ) : (
                <li
                  key={item.functionId}
                  className="menu-item"
                  onClick={(event) => {
                    handleMenuClick(event, 1, item?.functionId);
                    setCurrentFunctionId(item?.functionId);
                    dispatchUser({ type: "CURRENT_FUNCID", payload: item?.functionId });
                  }}
                >
                  <Link
                    key={item?.functionId}
                    className={`menu-link ${state.funcId === item?.functionId ? "active" : ""}`}
                    to={getMenuRouter(item?.functionId)}
                  >
                    <Icon className="menu-icon" />
                    {/* <img className='menu-icon' src={getMenuIcon(item?.functionId)} alt='左側選單圖示' /> */}
                    <span>{item?.name}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>

        {/* sidebar-footer 目前只有在mobile出現 */}
        <div className="sidebar-footer">
          <Link
            onClick={() => {
              handleLogout();
            }}
          >
            {t("logout")}
          </Link>
        </div>
      </div>
    </>
  );
}
